import Service from '@/Network/Services/Service'
import request from '@/Network/Requests/request'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import type {
  ApproveCheckValidationRequest,
  BulkApproveCheckValidationRequest,
  ExportCheckValidationParams,
  ListCheckValidationQueueParams,
  RejectCheckValidationRequest,
  ReverseCheckValidationRequest
} from '@/Network/Types/Requests/CheckValidationRequests'
import type {
  BulkApproveCheckValidationResponse,
  CheckValidationDetailResponse,
  CheckValidationHistoryResponse,
  CheckValidationQueueResponse
} from '@/Network/Types/Responses/CheckValidationResponses'

/**
 * Fila de avaliação de evidências (PRD 00) — `/check-validation`.
 *
 * O download do arquivo da evidência NÃO mora aqui: é
 * `Network.doctorCheckEvidence.downloadCheckEvidenceFile(stationId, evidenceId)`,
 * módulo próprio no backend para servir a evidência com a permissão do auditor.
 */
export default class CheckValidationService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/check-validation'
  }

  async listQueue(params: ListCheckValidationQueueParams): Promise<CheckValidationQueueResponse> {
    try {
      const { data } = await request.get<CheckValidationQueueResponse>(
        this.getCompletePath('/queue'),
        this.buildQueueQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os registros aguardando validação')
    }
  }

  async getDetail(checkId: number): Promise<CheckValidationDetailResponse> {
    try {
      const { data } = await request.get<CheckValidationDetailResponse>(
        this.getCompletePath(`/${checkId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes do registro')
    }
  }

  async getHistory(checkId: number): Promise<CheckValidationHistoryResponse> {
    try {
      const { data } = await request.get<CheckValidationHistoryResponse>(
        this.getCompletePath(`/${checkId}/history`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o histórico do registro')
    }
  }

  /** `PENDING_REVIEW → APPROVED`. 409 = versão desatualizada; 403 = registro do próprio usuário. */
  async approve(checkId: number, payload: ApproveCheckValidationRequest): Promise<void> {
    try {
      await request.patch<void>(this.getCompletePath(`/${checkId}/approve`), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar o registro')
    }
  }

  /** `PENDING_REVIEW → REJECTED` (terminal). */
  async reject(checkId: number, payload: RejectCheckValidationRequest): Promise<void> {
    try {
      await request.patch<void>(this.getCompletePath(`/${checkId}/reject`), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reprovar o registro')
    }
  }

  async bulkApprove(
    payload: BulkApproveCheckValidationRequest
  ): Promise<BulkApproveCheckValidationResponse> {
    try {
      const { data } = await request.patch<BulkApproveCheckValidationResponse>(
        this.getCompletePath('/bulk-approve'),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar os registros em lote')
    }
  }

  /** `APPROVED → PENDING_REVIEW`. */
  async reverse(checkId: number, payload: ReverseCheckValidationRequest): Promise<void> {
    try {
      await request.patch<void>(this.getCompletePath(`/${checkId}/reverse`), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reverter a avaliação do registro')
    }
  }

  async exportEvidences(params: ExportCheckValidationParams): Promise<void> {
    try {
      const query = this.buildExportQuery(params)
      const { data, headers } = await request.get<BlobPart>(
        this.getCompletePath('/export'),
        query,
        undefined,
        'blob'
      )
      const contentDisposition = headers?.['content-disposition'] ?? ''
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      const filename = filenameMatch?.[1] ?? `evidencias.${params.format === 'pdf' ? 'pdf' : 'zip'}`
      const contentType = params.format === 'pdf' ? 'application/pdf' : 'application/zip'
      const url = window.URL.createObjectURL(new Blob([data], { type: contentType }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível exportar as evidências')
    }
  }

  private buildExportQuery(params: ExportCheckValidationParams): Record<string, string | number> {
    const query: Record<string, string | number> = { format: params.format }
    if (params.startDate) query.startDate = params.startDate
    if (params.endDate) query.endDate = params.endDate
    if (params.uploadStartDate) query.uploadStartDate = params.uploadStartDate
    if (params.uploadEndDate) query.uploadEndDate = params.uploadEndDate
    if (params.statuses?.length) query.statuses = params.statuses.join(',')
    if (params.scheduleIds?.length) query.scheduleIds = params.scheduleIds.join(',')
    if (params.healthUnitIds?.length) query.healthUnitIds = params.healthUnitIds.join(',')
    if (params.withSummaryPage !== undefined) query.withSummaryPage = String(params.withSummaryPage)
    return query
  }

  /**
   * Só params preenchidos entram na query; arrays de ids viram CSV, que é o
   * formato do DTO do backend (`scheduleIds?: string`).
   */
  private buildQueueQuery(params: ListCheckValidationQueueParams): Record<string, string | number> {
    const query: Record<string, string | number> = {
      limit: params.limit,
      offset: params.offset
    }
    const search = params.search?.trim()
    if (search) query.search = search
    if (params.statuses) query.statuses = params.statuses
    if (params.onlyOverdue) query.onlyOverdue = 'true'
    if (params.scheduleIds?.length) query.scheduleIds = params.scheduleIds.join(',')
    if (params.healthUnitIds?.length) query.healthUnitIds = params.healthUnitIds.join(',')
    if (params.startDate) query.startDate = params.startDate
    if (params.endDate) query.endDate = params.endDate
    if (params.orderBy) query.orderBy = params.orderBy
    if (params.excludeCreatedBy) query.excludeCreatedBy = 'true'
    return query
  }
}
